import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, CurrencyDollarIcon, GlobeAltIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useCurrency } from '../../contexts/CurrencyContext';
import { cn } from '../../utils/cn';
import LoadingSpinner from '../ui/LoadingSpinner';

interface CurrencySelectorProps {
    value: string;
    onChange: (currency: string) => void;
    className?: string;
    placeholder?: string;
    disabled?: boolean;

    // Display options
    showFlags?: boolean;
    showCurrencyNames?: boolean;
    compact?: boolean;

    // Filtering options
    regional?: boolean; // Show region-specific currencies first
    popular?: boolean;  // Show popular currencies first
    exclude?: string[]; // Currencies to exclude
    include?: string[]; // Only show these currencies

    // Search and UI
    searchable?: boolean;
    maxHeight?: number;
    position?: 'auto' | 'top' | 'bottom';

    // Advanced options
    groupByRegion?: boolean;
    showRecentlyUsed?: boolean;
    onCurrencySelect?: (currency: { code: string; name: string; symbol: string }) => void;
}

interface CurrencyGroup {
    name: string;
    currencies: Array<{
        code: string;
        name: string;
        symbol: string;
        flag?: string;
        popular?: boolean;
    }>;
}

const POPULAR_CURRENCIES = ['USD', 'EUR', 'GBP', 'GHS', 'NGN', 'KES', 'ZAR', 'JPY', 'CNY', 'INR'];
const AFRICAN_CURRENCIES = ['GHS', 'NGN', 'KES', 'ZAR', 'EGP', 'MAD', 'TND', 'UGX', 'TZS', 'ZMW'];

const CurrencySelector: React.FC<CurrencySelectorProps> = ({
    value,
    onChange,
    className = '',
    placeholder = 'Select currency',
    disabled = false,
    showFlags = true,
    showCurrencyNames = true,
    compact = false,
    regional = false,
    popular = true,
    exclude = [],
    include,
    searchable = true,
    maxHeight = 300,
    position = 'auto',
    groupByRegion = false,
    showRecentlyUsed = true,
    onCurrencySelect,
}) => {
    const {
        availableCurrencies,
        getCurrencyInfo,
        isLoadingSupportedCurrencies,
        userCurrency,
    } = useCurrency();

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [recentlyUsed, setRecentlyUsed] = useState<string[]>([]);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Load recently used currencies from localStorage
    useEffect(() => {
        const stored = localStorage.getItem('sidego_recently_used_currencies');
        if (stored) {
            try {
                setRecentlyUsed(JSON.parse(stored).slice(0, 5));
            } catch {
                setRecentlyUsed([]);
            }
        }
    }, []);

    // Handle clicks outside dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchable && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [isOpen, searchable]);

    const addToRecentlyUsed = (currencyCode: string) => {
        const updated = [currencyCode, ...recentlyUsed.filter(c => c !== currencyCode)].slice(0, 5);
        setRecentlyUsed(updated);
        localStorage.setItem('sidego_recently_used_currencies', JSON.stringify(updated));
    };

    const handleCurrencySelect = (currencyCode: string) => {
        const currencyInfo = getCurrencyInfo(currencyCode);
        if (currencyInfo) {
            onChange(currencyCode);
            addToRecentlyUsed(currencyCode);
            onCurrencySelect?.(currencyInfo);
        }
        setIsOpen(false);
        setSearchTerm('');
    };

    const filterCurrencies = (currencies: typeof availableCurrencies) => {
        let filtered = currencies;

        // Apply include/exclude filters
        if (include && include.length > 0) {
            filtered = filtered.filter(c => include.includes(c.code));
        }
        if (exclude.length > 0) {
            filtered = filtered.filter(c => !exclude.includes(c.code));
        }

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(c =>
                c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.symbol.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return filtered;
    };

    const groupCurrencies = (currencies: typeof availableCurrencies): CurrencyGroup[] => {
        const filtered = filterCurrencies(currencies);
        const groups: CurrencyGroup[] = [];

        // Recently used group
        if (showRecentlyUsed && recentlyUsed.length > 0 && !searchTerm) {
            const recentCurrencies = recentlyUsed
                .map(code => filtered.find(c => c.code === code))
                .filter(Boolean)
                .map(c => ({ ...c!, popular: false }));

            if (recentCurrencies.length > 0) {
                groups.push({
                    name: 'Recently Used',
                    currencies: recentCurrencies
                });
            }
        }

        // Popular currencies group
        if (popular && !searchTerm) {
            const popularCurrencies = filtered
                .filter(c => POPULAR_CURRENCIES.includes(c.code))
                .sort((a, b) => POPULAR_CURRENCIES.indexOf(a.code) - POPULAR_CURRENCIES.indexOf(b.code))
                .map(c => ({ ...c, popular: true }));

            if (popularCurrencies.length > 0) {
                groups.push({
                    name: 'Popular',
                    currencies: popularCurrencies
                });
            }
        }

        // Regional grouping
        if (groupByRegion && !searchTerm) {
            const africanCurrencies = filtered
                .filter(c => AFRICAN_CURRENCIES.includes(c.code))
                .filter(c => !POPULAR_CURRENCIES.includes(c.code))
                .map(c => ({ ...c, popular: false }));

            if (africanCurrencies.length > 0) {
                groups.push({
                    name: 'Africa',
                    currencies: africanCurrencies
                });
            }

            // Other currencies
            const otherCurrencies = filtered
                .filter(c => !POPULAR_CURRENCIES.includes(c.code))
                .filter(c => !AFRICAN_CURRENCIES.includes(c.code))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(c => ({ ...c, popular: false }));

            if (otherCurrencies.length > 0) {
                groups.push({
                    name: 'Other',
                    currencies: otherCurrencies
                });
            }
        } else if (!groupByRegion) {
            // All other currencies
            const otherCurrencies = filtered
                .filter(c => !POPULAR_CURRENCIES.includes(c.code))
                .filter(c => !recentlyUsed.includes(c.code) || searchTerm)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(c => ({ ...c, popular: false }));

            if (otherCurrencies.length > 0) {
                groups.push({
                    name: searchTerm ? 'Results' : 'All Currencies',
                    currencies: otherCurrencies
                });
            }
        }

        return groups;
    };

    const selectedCurrency = getCurrencyInfo(value);

    const renderCurrencyOption = (currency: any, isSelected: boolean = false) => (
        <div
            key={currency.code}
            className={cn(
                'flex items-center px-3 py-2 cursor-pointer transition-colors',
                isSelected
                    ? 'bg-blue-50 dark:bg-blue-900 text-blue-600 dark:text-blue-300'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
            )}
            onClick={() => handleCurrencySelect(currency.code)}
        >
            {showFlags && currency.flag && (
                <img
                    src={currency.flag}
                    alt={currency.code}
                    className="w-5 h-5 mr-3 flex-shrink-0 rounded-sm"
                />
            )}
            <div className="flex-1 min-w-0">
                <div className="flex items-center">
                    <span className="font-medium text-sm">{currency.code}</span>
                    <span className="text-gray-400 dark:text-gray-500 text-sm ml-1">{currency.symbol}</span>
                    {currency.popular && (
                        <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded">
                            Popular
                        </span>
                    )}
                </div>
                {showCurrencyNames && !compact && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {currency.name}
                    </div>
                )}
            </div>
        </div>
    );

    const renderSelectedCurrency = () => {
        if (!selectedCurrency) {
            return (
                <div className="flex items-center text-gray-500 dark:text-gray-400">
                    <CurrencyDollarIcon className="w-5 h-5 mr-2" />
                    <span>{placeholder}</span>
                </div>
            );
        }

        return (
            <div className="flex items-center">
                {showFlags && selectedCurrency.flag && (
                    <img
                        src={selectedCurrency.flag}
                        alt={selectedCurrency.code}
                        className="w-5 h-5 mr-2 flex-shrink-0 rounded-sm"
                    />
                )}
                <span className="font-medium">{selectedCurrency.code}</span>
                <span className="text-gray-400 dark:text-gray-500 ml-1">{selectedCurrency.symbol}</span>
                {showCurrencyNames && !compact && (
                    <span className="text-gray-600 dark:text-gray-400 ml-2 truncate">
                        {selectedCurrency.name}
                    </span>
                )}
            </div>
        );
    };

    if (isLoadingSupportedCurrencies) {
        return (
            <div className={cn(
                'flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700',
                className
            )}>
                <LoadingSpinner size="sm" />
                <span className="ml-2 text-gray-500 dark:text-gray-400">Loading currencies...</span>
            </div>
        );
    }

    const groups = groupCurrencies(availableCurrencies);

    return (
        <div className={cn('relative', className)} ref={dropdownRef}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    'w-full flex items-center justify-between px-3 py-2 border rounded-md transition-colors',
                    disabled
                        ? 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:text-white',
                    compact && 'py-1.5'
                )}
            >
                {renderSelectedCurrency()}
                <ChevronDownIcon
                    className={cn(
                        'w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform',
                        isOpen && 'transform rotate-180'
                    )}
                />
            </button>

            {isOpen && (
                <div
                    className={cn(
                        'absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg',
                        position === 'top' && 'bottom-full mb-1 mt-0'
                    )}
                    style={{ maxHeight }}
                >
                    {searchable && (
                        <div className="p-3 border-b border-gray-200 dark:border-gray-600">
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search currencies..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    )}

                    <div className="max-h-64 overflow-y-auto">
                        {groups.length === 0 ? (
                            <div className="px-3 py-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                                No currencies found
                            </div>
                        ) : (
                            groups.map((group) => (
                                <div key={group.name}>
                                    {group.name && groups.length > 1 && (
                                        <div className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-t border-gray-100 dark:border-gray-700 first:border-t-0">
                                            {group.name}
                                        </div>
                                    )}
                                    {group.currencies.map((currency) =>
                                        renderCurrencyOption(currency, currency.code === value)
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CurrencySelector;